import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  inject,
  signal,
  OnInit,
  computed,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { BrnDialogImports } from '@spartan-ng/brain/dialog';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import { HlmSeparatorDirective } from '@spartan-ng/helm/separator';
import {
  BaseCreationDialog,
  BaseDialogConfig,
} from '@shared/components/base-creation-dialog/base-creation-dialog';
import { WizardDialogHeader } from '@shared/components/wizard-dialog-header/wizard-dialog-header';
import { WizardDialogFooter } from '@shared/components/wizard-dialog-footer/wizard-dialog-footer';
import { SuffixInput } from '@shared/components/suffix-input/suffix-input';
import { StepLead } from '@shared/components/step-lead/step-lead';
import { SummarySection, SummaryRow } from '@shared/components/summary-section/summary-section';
import { EquipmentStore } from '@features/catalog/store/equipment.store';
import {
  CreateStorageDto,
  SolarStorage,
} from '@features/catalog/models/storage.model';
import { ToastService } from '@shared/services/toast.service';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';

@Component({
  selector: 'app-create-storage-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ...BrnDialogImports,
    ...HlmDialogImports,
    HlmSeparatorDirective,
    WizardDialogHeader,
    WizardDialogFooter,
    SuffixInput,
    StepLead,
    SummarySection,
    SummaryRow,
  ],
  templateUrl: './create-storage-dialog.html',
  styleUrl: './create-storage-dialog.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateStorageDialogComponent
  extends BaseCreationDialog
  implements OnInit
{
  private readonly fb = inject(FormBuilder);
  private readonly equipmentStore = inject(EquipmentStore);
  private readonly toastService = inject(ToastService);
  private readonly currentOrgStore = inject(CurrentOrganizationStore);

  // Input for editing
  editStorage = input<SolarStorage | null>(null);

  // Override config input with specific dialog configuration
  override config = input<BaseDialogConfig>({
    title: 'Neuen Speicher erstellen',
    description:
      'Erstellen Sie einen neuen Stromspeicher mit Kapazität in kWh und Preis in EUR',
    stepLabels: ['Speicher Details', 'Bestätigung'],
    stepLeads: [
      'Technische Daten des Speichers.',
      'Prüfen Sie die Angaben vor dem Speichern.',
    ],
    submitButtonText: 'Speicher erstellen',
    eyebrow: 'Katalog · Speicher',
  });

  // Computed config based on edit mode for display purposes
  computedTitle = computed(() => {
    const editing = !!this.editStorage();
    return editing ? 'Speicher bearbeiten' : 'Neuen Speicher erstellen';
  });

  computedDescription = computed(() => {
    const editing = !!this.editStorage();
    return editing
      ? 'Bearbeiten Sie Kapazität (kWh) und Preis (EUR) des Speichers'
      : 'Erstellen Sie einen neuen Stromspeicher mit Kapazität (kWh) und Preis (EUR)';
  });

  computedSubmitButtonText = computed(() => {
    const editing = !!this.editStorage();
    return editing ? 'Speicher speichern' : 'Speicher erstellen';
  });

  // Outputs - delegate to the base class's submitted output
  storageCreated = output<SolarStorage>();
  storageUpdated = output<SolarStorage>();

  // Form
  storageForm: FormGroup;

  constructor() {
    super();
    this.storageForm = this.fb.group({
      name: ['', [Validators.required, Validators.pattern(/\S/), Validators.minLength(3)]],
      capacity: ['', [Validators.required, Validators.min(0.1)]],
      price: ['', [Validators.required, Validators.min(0)]],
    });

    // Use effect to populate form when editStorage changes
    effect(() => {
      const storage = this.editStorage();
      if (storage) {
        this.populateForm(storage);
      }
    });
  }

  ngOnInit() {
    // Initialization if needed
  }

  private populateForm(storage: SolarStorage | null): void {
    if (storage) {
      this.storageForm.patchValue({
        name: storage.name,
        capacity: storage.size,
        price: storage.cost,
      });
    }
  }

  override canProceedFromCurrentStep(): boolean {
    const step = this.currentStep();
    switch (step) {
      case 1:
        // Step 1: Check storage details (name, capacity, price)
        return (
          this.storageForm.get('name')?.valid === true &&
          this.storageForm.get('capacity')?.valid === true &&
          this.storageForm.get('price')?.valid === true
        );
      case 2:
        // Step 2: All fields valid
        return this.storageForm.valid;
      default:
        return false;
    }
  }

  // Reset dialog state
  protected override resetDialog(): void {
    super.resetDialog();
    this.storageForm.reset();
  }

  // Handle submission
  protected override async handleSubmit(dialogContext: any): Promise<void> {
    if (!this.storageForm.valid) {
      throw new Error('Bitte füllen Sie alle erforderlichen Felder aus');
    }

    const formData = this.storageForm.value;
    const payload: CreateStorageDto = {
      name: formData.name.trim(),
      size: Number(formData.capacity),
      cost: Number(formData.price),
    };

    const orgId = this.currentOrgStore.organizationId();
    if (!orgId) {
      throw new Error('Organisations-ID nicht verfügbar');
    }

    const editingStorage = this.editStorage();
    let result: SolarStorage;

    if (editingStorage) {
      // Update mode
      result = await this.equipmentStore.updateStorage(
        orgId,
        editingStorage.id,
        payload,
      );
      this.toastService.success('Speicher erfolgreich aktualisiert');
      this.submitted.emit(result);
      this.storageUpdated.emit(result);
    } else {
      // Create mode
      result = await this.equipmentStore.createStorage(orgId, payload);
      if (!result) {
        throw new Error('Fehler beim Erstellen des Speichers');
      }
      this.toastService.success('Speicher erfolgreich erstellt');
      this.submitted.emit(result);
      this.storageCreated.emit(result);
    }
  }
}
